import {
  RaidPlanActor,
  RaidPlanAssignmentEntity,
  RaidPlanCast,
  RaidPlanEncounterAssignmentAbility,
} from 'app/frontend/types/custom/RaidPlan'
import { EncounterAbilityRow } from '../../TeamPlans/hooks/useTeamPlanComputedData'
import {
  AssignmentViewMode,
  getAssignmentTypeInterruptRole,
  getEffectiveAbilityCustomGrouping,
  getEffectiveAssignmentGroups,
  getEffectiveAssignmentType,
  getEffectiveAssignmentViewMode,
  getEffectiveExpectedEntities,
  getAssignmentScopeIndexes,
  isImmunityAssignmentType,
  isInterruptAssignmentType,
  normalizeAssignmentBox,
  normalizeAssignmentCast,
} from '../../TeamPlans/components/assignmentTimeline'
import {
  getAllSpecializations,
  getSpecIdForClassRole,
  getSpecialization,
  getSpecializationImmunity,
  getSpecializationInterruptCooldown,
} from 'app/frontend/base/services/characterValues'
import type { SpecImmunity } from 'app/frontend/types/character_values'
import { EventActivityParticipantEntry } from './eventActivityParticipantEntry'
import { resolveEventParticipantCharacter } from './eventActivityParticipants'
import {
  EncounterAssignmentEntityOverride,
  EventPlanCastTargetOverrides,
  getCastTargetEntityOverride,
} from './eventActivityPlanOverrides'
import { unavailableParticipantStatuses } from 'app/frontend/types/custom/EventParticipantStatus'
import {
  collectionRoleMatcher,
  getAnyPlayerFillerCount,
  getStoredSlotWeight,
} from '../../TeamPlans/components/assignmentEntityCounting'

type RoleKey =
  | 'tank'
  | 'healer'
  | 'dps'
  | 'ranged'
  | 'melee'
  | 'anyone'
  | 'non_tank'
  | 'anyone_ranged'
  | 'anyone_melee'

type PreferredDistance = 'ranged' | 'melee'

const isFlexibleRole = (role: RoleKey) =>
  role === 'anyone' ||
  role === 'non_tank' ||
  role === 'anyone_ranged' ||
  role === 'anyone_melee'

const requestedDistanceForRole = (
  role: RoleKey,
): PreferredDistance | undefined => {
  if (role === 'anyone_ranged') return 'ranged'
  if (role === 'anyone_melee') return 'melee'
  return undefined
}

const getParticipantPreferredDistance = (
  participant: ResolvedAssignmentParticipant,
): PreferredDistance | undefined => {
  if (participant.role === 'healer' || participant.role === 'tank') {
    if (participant.specId != null) {
      const distance = getSpecialization(
        participant.specId.toString(),
      )?.preferred_distance
      if (distance === 'ranged' || distance === 'melee') return distance
    }
    return participant.role === 'tank' ? 'melee' : undefined
  }
  if (participant.role === 'ranged') return 'ranged'
  if (participant.role === 'melee') return 'melee'
  return undefined
}

const matchesPreferredDistance = (
  participant: ResolvedAssignmentParticipant,
  distance: PreferredDistance,
) => {
  const preferred = getParticipantPreferredDistance(participant)
  return preferred === undefined || preferred === distance
}

export type ResolvedAssignmentParticipant = {
  participantId: number
  characterId: number
  classId?: number
  role: RoleKey
  specId: number | null
  name: string
  characterClass: wowaudit.CharacterReference['characterClass']
}

export type ResolvedAssignmentEntity =
  | {
      kind: 'participant'
      participant: ResolvedAssignmentParticipant
      sourceEntity: RaidPlanAssignmentEntity
      issue?: EventActivityTimelineResolverIssue
      isOverridden?: boolean
    }
  | {
      kind: 'aggregate'
      sourceEntity: RaidPlanAssignmentEntity
      issue?: EventActivityTimelineResolverIssue
      isOverridden?: boolean
    }
  | {
      kind: 'plain'
      sourceEntity: RaidPlanAssignmentEntity
      issue?: EventActivityTimelineResolverIssue
      isOverridden?: boolean
    }
  | {
      kind: 'ignored'
      sourceEntity: RaidPlanAssignmentEntity
      issue?: EventActivityTimelineResolverIssue
      isOverridden?: boolean
    }

export type EventActivityTimelineResolverIssue = {
  type: 'insufficient_candidates' | 'unmatched_member'
  spellId: number
  scopeId: string
  role?: RoleKey
  requested: number
  available: number
  missing: number
  memberName?: string
}

export type EventActivityAutoMatchMode = 'full' | 'preferred_and_explicit'

type ResolveEventActivityTimelineAssignmentsInput = {
  activityId: number
  entries: EventActivityParticipantEntry[]
  encounterRows: EncounterAbilityRow[]
  abilities: RaidPlanEncounterAssignmentAbility[]
  autoMatchMode?: EventActivityAutoMatchMode
  spreadRoleCollections?: boolean
}

type ResolveEventActivityTimelineActorsInput = {
  activityId: number
  entries: EventActivityParticipantEntry[]
  actors: RaidPlanActor[]
  autoMatchMode?: EventActivityAutoMatchMode
}

export type ResolvedTimelineActor = {
  actor: RaidPlanActor
  participant?: ResolvedAssignmentParticipant
  issue?: EventActivityTimelineActorResolverIssue
  isOverridden?: boolean
  isIgnored?: boolean
}

export type EventActivityTimelineActorResolverIssue = {
  type: 'unmatched_actor'
  actorLabel: string
  requested: number
  available: number
  missing: number
}

const asResolverRole = (role: string): RoleKey => {
  if (role === 'tank') return 'tank'
  if (role === 'heal' || role === 'healer') return 'healer'
  if (role === 'melee') return 'melee'
  if (role === 'ranged') return 'ranged'
  if (role === 'dps') return 'dps'
  if (role === 'anyone' || role === 'any_player') return 'anyone'
  if (role === 'non_tank' || role === 'nontank' || role === 'any_non_tank') {
    return 'non_tank'
  }
  if (
    role === 'anyone_ranged' ||
    role === 'anyoneranged' ||
    role === 'any_ranged'
  ) {
    return 'anyone_ranged'
  }
  if (
    role === 'anyone_melee' ||
    role === 'anyonemelee' ||
    role === 'any_melee'
  ) {
    return 'anyone_melee'
  }
  return 'anyone'
}

const matchesRole = (
  participantRole: RoleKey,
  requestedRole: RoleKey,
): boolean => {
  if (requestedRole === 'anyone') return true
  if (requestedRole === 'non_tank') return participantRole !== 'tank'
  if (requestedRole === 'dps') {
    return participantRole === 'melee' || participantRole === 'ranged'
  }
  return participantRole === requestedRole
}

export const toResolvedParticipants = (
  entries: EventActivityParticipantEntry[],
  activityId: number,
) => {
  return entries
    .filter(
      (entry) =>
        !unavailableParticipantStatuses.includes(entry.participant.status),
    )
    .filter((entry) => entry.selections[activityId]?.selected)
    .map((entry) => {
      const selection = entry.selections[activityId]
      const character = resolveEventParticipantCharacter(entry, activityId)
      const isMainSelection =
        selection.characterId === entry.characterId &&
        selection.role === entry.role &&
        selection.specId === entry.specId
      return {
        participant: {
          participantId: entry.participant.id,
          characterId: character.id,
          classId: character.classId,
          role: asResolverRole(selection.role),
          specId: selection.specId,
          name: character.name,
          characterClass: character.characterClass,
        },
        isMainSelection,
      }
    })
    .sort((left, right) => {
      if (left.isMainSelection !== right.isMainSelection) {
        return Number(right.isMainSelection) - Number(left.isMainSelection)
      }
      return left.participant.name.localeCompare(right.participant.name)
    })
    .map((entry) => entry.participant)
}

const pickFirstUnusedParticipant = (
  participants: ResolvedAssignmentParticipant[],
  usedParticipantIds: Set<number>,
  matches: (participant: ResolvedAssignmentParticipant) => boolean,
) => {
  return participants.find(
    (participant) =>
      !usedParticipantIds.has(participant.participantId) &&
      matches(participant),
  )
}

export const resolveEventActivityTimelineActors = ({
  activityId,
  entries,
  actors,
  autoMatchMode = 'full',
}: ResolveEventActivityTimelineActorsInput) => {
  const participants = toResolvedParticipants(entries, activityId)
  const roleBySpecId = Object.entries(getAllSpecializations()).reduce<
    Record<number, RoleKey>
  >((acc, [specId, specialization]) => {
    acc[Number(specId)] = asResolverRole(specialization.default_role)
    return acc
  }, {})
  const usedParticipantIds = new Set<number>()
  const resolvedActors: ResolvedTimelineActor[] = actors.map((actor) => ({
    actor,
  }))

  resolvedActors.forEach((resolvedActor) => {
    const preferredCharacterId = resolvedActor.actor.preferred_character_id
    if (typeof preferredCharacterId !== 'number') return
    const preferredParticipant = pickFirstUnusedParticipant(
      participants,
      usedParticipantIds,
      (participant) => participant.characterId === preferredCharacterId,
    )
    if (!preferredParticipant) return
    resolvedActor.participant = preferredParticipant
    usedParticipantIds.add(preferredParticipant.participantId)
  })

  if (autoMatchMode === 'preferred_and_explicit') {
    const issues = resolvedActors
      .filter((resolvedActor) => !resolvedActor.participant)
      .map((resolvedActor) => {
        const issue: EventActivityTimelineActorResolverIssue = {
          type: 'unmatched_actor',
          actorLabel: resolvedActor.actor.label,
          requested: 1,
          available: 0,
          missing: 1,
        }
        resolvedActor.issue = issue
        return issue
      })
    return {
      resolvedActors,
      issues,
    }
  }

  resolvedActors.forEach((resolvedActor) => {
    if (resolvedActor.participant) return
    if (resolvedActor.actor.spec_id === null) return
    const specParticipant = pickFirstUnusedParticipant(
      participants,
      usedParticipantIds,
      (participant) => participant.specId === resolvedActor.actor.spec_id,
    )
    if (!specParticipant) return
    resolvedActor.participant = specParticipant
    usedParticipantIds.add(specParticipant.participantId)
  })

  resolvedActors.forEach((resolvedActor) => {
    if (resolvedActor.participant) return
    if (resolvedActor.actor.spec_id === null) return
    if (resolvedActor.actor.casts.length === 0) return
    const actorRole = roleBySpecId[resolvedActor.actor.spec_id]
    if (!actorRole) return
    const classRoleParticipant = pickFirstUnusedParticipant(
      participants,
      usedParticipantIds,
      (participant) =>
        participant.classId === resolvedActor.actor.class_id &&
        participant.specId === null &&
        matchesRole(participant.role, actorRole),
    )
    if (!classRoleParticipant) return
    resolvedActor.participant = classRoleParticipant
    usedParticipantIds.add(classRoleParticipant.participantId)
  })

  resolvedActors.forEach((resolvedActor) => {
    if (resolvedActor.participant) return
    if (resolvedActor.actor.spec_id !== null) return
    const classParticipant = pickFirstUnusedParticipant(
      participants,
      usedParticipantIds,
      (participant) => participant.classId === resolvedActor.actor.class_id,
    )
    if (!classParticipant) return
    resolvedActor.participant = classParticipant
    usedParticipantIds.add(classParticipant.participantId)
  })

  const issues = resolvedActors
    .filter((resolvedActor) => !resolvedActor.participant)
    .map((resolvedActor) => {
      const available =
        resolvedActor.actor.spec_id !== null
          ? participants.filter(
              (participant) =>
                !usedParticipantIds.has(participant.participantId) &&
                participant.specId === resolvedActor.actor.spec_id,
            ).length
          : participants.filter(
              (participant) =>
                !usedParticipantIds.has(participant.participantId) &&
                participant.classId === resolvedActor.actor.class_id,
            ).length
      const issue: EventActivityTimelineActorResolverIssue = {
        type: 'unmatched_actor',
        actorLabel: resolvedActor.actor.label,
        requested: 1,
        available,
        missing: 1,
      }
      resolvedActor.issue = issue
      return issue
    })

  return {
    resolvedActors,
    issues,
  }
}

type InterruptContext = {
  preferredRole: 'ranged' | 'melee' | null
  cooldownByParticipantId: Record<number, number>
}

const participantInterruptCooldown = (
  participant: ResolvedAssignmentParticipant,
) => {
  const specId =
    participant.specId ??
    getSpecIdForClassRole(participant.classId, participant.role)
  return getSpecializationInterruptCooldown(specId)
}

type ImmunityContext = {
  requireMobile: boolean
  byParticipantId: Record<number, SpecImmunity>
  usageTimesByParticipantId: Record<number, number[]>
  scopeTime: number
}

const participantImmunity = (
  participant: ResolvedAssignmentParticipant,
): SpecImmunity | null => {
  const specId =
    participant.specId ??
    getSpecIdForClassRole(participant.classId, participant.role)
  return getSpecializationImmunity(specId)
}

const immunityChargesAvailableAt = (
  usages: number[],
  atTime: number,
  cooldown: number,
  maxCharges: number,
) => {
  let available = maxCharges
  let rechargeCompleteAt: number | null = null
  const usageTimes = usages
    .filter((usageTime) => usageTime <= atTime)
    .sort((left, right) => left - right)

  const applyRechargesUntil = (time: number) => {
    while (
      rechargeCompleteAt !== null &&
      rechargeCompleteAt <= time &&
      available < maxCharges
    ) {
      available += 1
      rechargeCompleteAt =
        available < maxCharges ? rechargeCompleteAt + cooldown : null
    }
  }

  usageTimes.forEach((usageTime) => {
    applyRechargesUntil(usageTime)
    available -= 1
    if (rechargeCompleteAt === null && available < maxCharges) {
      rechargeCompleteAt = usageTime + cooldown
    }
  })
  applyRechargesUntil(atTime)
  return available
}

const createInterruptContext = (
  assignmentType: ReturnType<typeof getEffectiveAssignmentType>,
  participants: ResolvedAssignmentParticipant[],
): InterruptContext | undefined => {
  if (!isInterruptAssignmentType(assignmentType)) return undefined
  const cooldownByParticipantId = participants.reduce<Record<number, number>>(
    (acc, participant) => {
      const cooldown = participantInterruptCooldown(participant)
      if (cooldown === null) return acc
      acc[participant.participantId] = cooldown
      return acc
    },
    {},
  )
  return {
    preferredRole: getAssignmentTypeInterruptRole(assignmentType),
    cooldownByParticipantId,
  }
}

const createImmunityContext = (
  assignmentType: ReturnType<typeof getEffectiveAssignmentType>,
  participants: ResolvedAssignmentParticipant[],
): ImmunityContext | undefined => {
  if (!isImmunityAssignmentType(assignmentType)) return undefined
  const byParticipantId = participants.reduce<Record<number, SpecImmunity>>(
    (acc, participant) => {
      const immunity = participantImmunity(participant)
      if (!immunity) return acc
      acc[participant.participantId] = immunity
      return acc
    },
    {},
  )
  return {
    requireMobile: assignmentType === 'mobile_immunity',
    byParticipantId,
    usageTimesByParticipantId: {},
    scopeTime: 0,
  }
}

const createRoundRobinPicker = (
  participants: ResolvedAssignmentParticipant[],
  rolePriority: RoleKey[],
  interrupt?: InterruptContext,
  immunity?: ImmunityContext,
) => {
  const participantIdsByRole: Record<RoleKey, number[]> = {
    tank: participants
      .filter((participant) => matchesRole(participant.role, 'tank'))
      .map((participant) => participant.participantId),
    healer: participants
      .filter((participant) => matchesRole(participant.role, 'healer'))
      .map((participant) => participant.participantId),
    melee: participants
      .filter((participant) => matchesRole(participant.role, 'melee'))
      .map((participant) => participant.participantId),
    ranged: participants
      .filter((participant) => matchesRole(participant.role, 'ranged'))
      .map((participant) => participant.participantId),
    dps: participants
      .filter((participant) => matchesRole(participant.role, 'dps'))
      .map((participant) => participant.participantId),
    anyone: participants.map((participant) => participant.participantId),
    non_tank: participants
      .filter((participant) => matchesRole(participant.role, 'non_tank'))
      .map((participant) => participant.participantId),
    anyone_ranged: participants
      .filter((participant) => matchesPreferredDistance(participant, 'ranged'))
      .map((participant) => participant.participantId),
    anyone_melee: participants
      .filter((participant) => matchesPreferredDistance(participant, 'melee'))
      .map((participant) => participant.participantId),
  }
  const participantById = participants.reduce<
    Record<number, ResolvedAssignmentParticipant>
  >((acc, participant) => {
    acc[participant.participantId] = participant
    return acc
  }, {})
  const cursorByRole: Record<RoleKey, number> = {
    tank: 0,
    healer: 0,
    melee: 0,
    ranged: 0,
    dps: 0,
    anyone: 0,
    non_tank: 0,
    anyone_ranged: 0,
    anyone_melee: 0,
  }
  const participantIdsByClass = participants.reduce<Record<number, number[]>>(
    (acc, participant) => {
      if (!participant.classId) return acc
      acc[participant.classId] = acc[participant.classId] ?? []
      acc[participant.classId].push(participant.participantId)
      return acc
    },
    {},
  )
  const cursorByClass: Record<number, number> = {}
  const timesPickedByParticipantId: Record<number, number> = {}

  const isCapable = (candidateId: number) => {
    if (
      interrupt &&
      interrupt.cooldownByParticipantId[candidateId] === undefined
    ) {
      return false
    }
    if (immunity) {
      const specImmunity = immunity.byParticipantId[candidateId]
      if (!specImmunity) return false
      if (immunity.requireMobile && !specImmunity.mobile) return false
      return (
        immunityChargesAvailableAt(
          immunity.usageTimesByParticipantId[candidateId] ?? [],
          immunity.scopeTime,
          specImmunity.cooldown,
          specImmunity.charges,
        ) > 0
      )
    }
    return true
  }

  const recordImmunityUsage = (candidateId: number) => {
    if (!immunity) return
    const usages = immunity.usageTimesByParticipantId[candidateId] ?? []
    usages.push(immunity.scopeTime)
    immunity.usageTimesByParticipantId[candidateId] = usages
  }

  const setScopeTime = (time: number) => {
    if (immunity) immunity.scopeTime = time
  }

  const countAvailableFromIds = (
    ids: number[],
    usedInScope: Set<number>,
    blockedParticipantIds: Set<number>,
  ) =>
    ids.filter(
      (candidateId) =>
        !usedInScope.has(candidateId) &&
        !blockedParticipantIds.has(candidateId) &&
        isCapable(candidateId),
    ).length

  const pickFromIds = (
    ids: number[],
    getCursor: () => number,
    setCursor: (nextCursor: number) => void,
    usedInScope: Set<number>,
    blockedParticipantIds: Set<number>,
  ): ResolvedAssignmentParticipant | undefined => {
    if (ids.length === 0) return undefined
    if (interrupt) {
      const candidates = ids.flatMap((candidateId, poolIndex) => {
        if (usedInScope.has(candidateId)) return []
        if (blockedParticipantIds.has(candidateId)) return []
        const cooldown = interrupt.cooldownByParticipantId[candidateId]
        if (cooldown === undefined) return []
        const participant = participantById[candidateId]
        if (!participant) return []
        const roleMismatch =
          interrupt.preferredRole &&
          !matchesRole(participant.role, interrupt.preferredRole)
            ? 1
            : 0
        return [
          {
            participant,
            roleMismatch,
            timesPicked: timesPickedByParticipantId[candidateId] ?? 0,
            cooldown,
            poolIndex,
          },
        ]
      })
      candidates.sort((left, right) => {
        if (left.roleMismatch !== right.roleMismatch) {
          return left.roleMismatch - right.roleMismatch
        }
        if (left.timesPicked !== right.timesPicked) {
          return left.timesPicked - right.timesPicked
        }
        if (left.cooldown !== right.cooldown) {
          return left.cooldown - right.cooldown
        }
        return left.poolIndex - right.poolIndex
      })
      const next = candidates[0]
      if (!next) return undefined
      timesPickedByParticipantId[next.participant.participantId] =
        (timesPickedByParticipantId[next.participant.participantId] ?? 0) + 1
      return next.participant
    }
    for (let offset = 0; offset < ids.length; offset += 1) {
      const candidateIndex = (getCursor() + offset) % ids.length
      const candidateId = ids[candidateIndex]
      if (usedInScope.has(candidateId)) continue
      if (blockedParticipantIds.has(candidateId)) continue
      if (!isCapable(candidateId)) continue
      setCursor((candidateIndex + 1) % ids.length)
      recordImmunityUsage(candidateId)
      return participantById[candidateId]
    }
    return undefined
  }

  const pickFromRole = (
    role: RoleKey,
    usedInScope: Set<number>,
    blockedParticipantIds: Set<number> = new Set<number>(),
  ): ResolvedAssignmentParticipant | undefined =>
    pickFromIds(
      participantIdsByRole[role],
      () => cursorByRole[role],
      (nextCursor) => {
        cursorByRole[role] = nextCursor
      },
      usedInScope,
      blockedParticipantIds,
    )

  const pickClass = (
    classId: number,
    usedInScope: Set<number>,
    blockedParticipantIds: Set<number> = new Set<number>(),
  ): ResolvedAssignmentParticipant | undefined =>
    pickFromIds(
      participantIdsByClass[classId] ?? [],
      () => cursorByClass[classId] ?? 0,
      (nextCursor) => {
        cursorByClass[classId] = nextCursor
      },
      usedInScope,
      blockedParticipantIds,
    )

  const skipsFlexiblePriorityRole = (
    requested: RoleKey,
    prioritized: RoleKey,
  ) => {
    if (isFlexibleRole(prioritized)) return true
    if (requested === 'non_tank' && prioritized === 'tank') return true
    if (requested === 'anyone_ranged' && prioritized === 'melee') return true
    if (requested === 'anyone_melee' && prioritized === 'ranged') return true
    return false
  }

  const idsForPriorityRole = (requested: RoleKey, prioritized: RoleKey) => {
    const ids = participantIdsByRole[prioritized]
    const distance = requestedDistanceForRole(requested)
    if (!distance) return ids
    return ids.filter((candidateId) => {
      const participant = participantById[candidateId]
      return participant
        ? matchesPreferredDistance(participant, distance)
        : false
    })
  }

  const pickPriorityRole = (
    requested: RoleKey,
    prioritized: RoleKey,
    usedInScope: Set<number>,
    blockedParticipantIds: Set<number>,
  ) =>
    pickFromIds(
      idsForPriorityRole(requested, prioritized),
      () => cursorByRole[prioritized],
      (nextCursor) => {
        cursorByRole[prioritized] = nextCursor
      },
      usedInScope,
      blockedParticipantIds,
    )

  const pick = (
    role: RoleKey,
    usedInScope: Set<number>,
    blockedParticipantIds: Set<number> = new Set<number>(),
    boxRoleCounts?: Partial<Record<RoleKey, number>>,
  ): ResolvedAssignmentParticipant | undefined => {
    if (!isFlexibleRole(role)) {
      return pickFromRole(role, usedInScope, blockedParticipantIds)
    }
    if (interrupt) {
      return pickFromRole(role, usedInScope, blockedParticipantIds)
    }
    if (boxRoleCounts) {
      let selectedRole: RoleKey | undefined
      let selectedCount = Number.POSITIVE_INFINITY
      for (const prioritizedRole of rolePriority) {
        if (
          skipsFlexiblePriorityRole(role, prioritizedRole) ||
          prioritizedRole === 'dps'
        ) {
          continue
        }
        const available = countAvailableFromIds(
          idsForPriorityRole(role, prioritizedRole),
          usedInScope,
          blockedParticipantIds,
        )
        if (available === 0) continue
        const count = boxRoleCounts[prioritizedRole] ?? 0
        if (count < selectedCount) {
          selectedCount = count
          selectedRole = prioritizedRole
        }
      }
      if (selectedRole) {
        return pickPriorityRole(
          role,
          selectedRole,
          usedInScope,
          blockedParticipantIds,
        )
      }
      return pickFromRole(role, usedInScope, blockedParticipantIds)
    }
    for (const prioritizedRole of rolePriority) {
      if (skipsFlexiblePriorityRole(role, prioritizedRole)) continue
      const participant = pickPriorityRole(
        role,
        prioritizedRole,
        usedInScope,
        blockedParticipantIds,
      )
      if (participant) return participant
    }
    return pickFromRole(role, usedInScope, blockedParticipantIds)
  }

  const availableByRole = (role: RoleKey): number =>
    participantIdsByRole[role].filter(isCapable).length
  const availableByClass = (classId: number): number =>
    (participantIdsByClass[classId] ?? []).filter(isCapable).length
  const availableAssignableByClass = (
    classId: number,
    usedInScope: Set<number>,
    blockedParticipantIds: Set<number>,
  ) =>
    countAvailableFromIds(
      participantIdsByClass[classId] ?? [],
      usedInScope,
      blockedParticipantIds,
    )
  const availableAssignableByRole = (
    role: RoleKey,
    usedInScope: Set<number>,
    blockedParticipantIds: Set<number>,
  ) =>
    countAvailableFromIds(
      participantIdsByRole[role],
      usedInScope,
      blockedParticipantIds,
    )

  const pickMember = (
    member: Extract<RaidPlanAssignmentEntity, { kind: 'member' }>,
    usedInScope: Set<number>,
    blockedParticipantIds: Set<number> = new Set<number>(),
  ): ResolvedAssignmentParticipant | undefined => {
    const sameCharacterParticipant = participants.find(
      (participant) =>
        participant.characterId === member.character_id &&
        !usedInScope.has(participant.participantId),
    )
    if (sameCharacterParticipant) return sameCharacterParticipant
    if (member.spec_id !== null) {
      const sameSpecParticipant = participants.find(
        (participant) =>
          participant.specId === member.spec_id &&
          !usedInScope.has(participant.participantId) &&
          !blockedParticipantIds.has(participant.participantId),
      )
      if (sameSpecParticipant) return sameSpecParticipant
    }
    const memberRole = asResolverRole(member.role)
    return participants.find(
      (participant) =>
        matchesRole(participant.role, memberRole) &&
        !usedInScope.has(participant.participantId) &&
        !blockedParticipantIds.has(participant.participantId),
    )
  }

  const availableForMember = (
    member: Extract<RaidPlanAssignmentEntity, { kind: 'member' }>,
  ) => {
    const sameCharacterCount = participants.filter(
      (participant) => participant.characterId === member.character_id,
    ).length
    if (sameCharacterCount > 0) return sameCharacterCount
    if (member.spec_id !== null) {
      const sameSpecCount = participants.filter(
        (participant) => participant.specId === member.spec_id,
      )
      if (sameSpecCount.length > 0) return sameSpecCount.length
    }
    const memberRole = asResolverRole(member.role)
    return participants.filter((participant) =>
      matchesRole(participant.role, memberRole),
    ).length
  }

  return {
    pick,
    pickClass,
    pickMember,
    availableForMember,
    availableByRole,
    availableByClass,
    availableAssignableByClass,
    availableAssignableByRole,
    setScopeTime,
  }
}

const waterFill = (total: number, capacities: number[]) => {
  const targets = capacities.map(() => 0)
  let remaining = Math.max(total, 0)
  let allocated = true
  while (remaining > 0 && allocated) {
    allocated = false
    for (let index = 0; index < capacities.length; index += 1) {
      if (remaining <= 0) break
      if (targets[index] >= capacities[index]) continue
      targets[index] += 1
      remaining -= 1
      allocated = true
    }
  }
  return targets
}

const expandedBoxSlotCount = (
  box: ReturnType<typeof normalizeAssignmentBox>,
  abilityExpectedEntities?: number,
) => {
  const expected = getEffectiveExpectedEntities(
    abilityExpectedEntities,
    box.expected_entities,
  )
  const { disabling, weight } = getStoredSlotWeight(box.entities)
  if (disabling || typeof expected !== 'number') return weight
  return Math.max(expected, weight)
}

const toIgnoredResolvedEntity = (
  entity: RaidPlanAssignmentEntity,
  isOverridden?: boolean,
): ResolvedAssignmentEntity => ({
  kind: 'ignored',
  sourceEntity: entity,
  isOverridden,
})

const expandSpreadRoleCollections = (
  resolved: ResolvedAssignmentEntity[],
  participants: ResolvedAssignmentParticipant[],
  usedParticipantIds: Set<number>,
): ResolvedAssignmentEntity[] => {
  const usedInBox = new Set(
    resolved.flatMap((entity) =>
      entity.kind === 'participant' ? [entity.participant.participantId] : [],
    ),
  )
  return resolved.flatMap((entity): ResolvedAssignmentEntity[] => {
    if (entity.kind !== 'aggregate') return [entity]
    const matcher = collectionRoleMatcher(entity.sourceEntity)
    if (!matcher) return [entity]
    const matching = participants.filter(
      (participant) =>
        matcher(participant.role) && !usedInBox.has(participant.participantId),
    )
    if (matching.length === 0) return [entity]
    matching.forEach((participant) => {
      usedInBox.add(participant.participantId)
      usedParticipantIds.add(participant.participantId)
    })
    return matching.map((participant) => ({
      kind: 'participant' as const,
      participant,
      sourceEntity: entity.sourceEntity,
    }))
  })
}

const applyExcessIgnoredSlots = ({
  boxes,
  resolvedByBox,
  ignoreCount,
}: {
  boxes: ReturnType<typeof normalizeAssignmentBox>[]
  resolvedByBox: (ResolvedAssignmentEntity | null)[][]
  ignoreCount: number
}) => {
  if (ignoreCount <= 0) return
  const ignorableSlotIndexesByBox = boxes.map((box, boxIndex) =>
    box.entities.flatMap((entity, slotIndex) => {
      if (entity.kind === 'member') return []
      const resolved = resolvedByBox[boxIndex][slotIndex]
      if (
        resolved?.kind === 'participant' ||
        resolved?.kind === 'aggregate' ||
        resolved?.kind === 'ignored'
      ) {
        return []
      }
      return [slotIndex]
    }),
  )
  const ignoreTargets = waterFill(
    ignoreCount,
    ignorableSlotIndexesByBox.map((slotIndexes) => slotIndexes.length),
  )
  ignorableSlotIndexesByBox.forEach((slotIndexes, boxIndex) => {
    const fromEnd = [...slotIndexes].sort((left, right) => right - left)
    fromEnd.slice(0, ignoreTargets[boxIndex]).forEach((slotIndex) => {
      resolvedByBox[boxIndex][slotIndex] = toIgnoredResolvedEntity(
        boxes[boxIndex].entities[slotIndex],
      )
    })
  })
}

type DeferredResolverEntity =
  | Extract<RaidPlanAssignmentEntity, { kind: 'by_role' }>
  | Extract<RaidPlanAssignmentEntity, { kind: 'by_class' }>

const resolveAssignmentEntityWithPicker = ({
  entity,
  picker,
  usedParticipantIds,
  blockedParticipantIds,
  spellId,
  scopeId,
  availableAnyoneCount,
  participantByCharacterId,
  override,
  deferAssignableEntity = false,
  autoMatchMode = 'full',
  onMemberParticipantResolved,
  boxRoleCounts,
}: {
  entity: RaidPlanAssignmentEntity
  picker: ReturnType<typeof createRoundRobinPicker>
  usedParticipantIds: Set<number>
  blockedParticipantIds?: Set<number>
  spellId: number
  scopeId: string
  availableAnyoneCount: number
  participantByCharacterId?: Record<number, ResolvedAssignmentParticipant>
  override?: EncounterAssignmentEntityOverride
  deferAssignableEntity?: boolean
  autoMatchMode?: EventActivityAutoMatchMode
  onMemberParticipantResolved?: (
    participant: ResolvedAssignmentParticipant,
    memberEntity: Extract<RaidPlanAssignmentEntity, { kind: 'member' }>,
  ) => void
  boxRoleCounts?: Partial<Record<RoleKey, number>>
}) => {
  const blocked = blockedParticipantIds ?? new Set<number>()
  if (
    override?.kind === 'participant' &&
    participantByCharacterId &&
    participantByCharacterId[override.characterId]
  ) {
    const participant = participantByCharacterId[override.characterId]
    usedParticipantIds.add(participant.participantId)
    return {
      resolvedEntity: {
        kind: 'participant',
        participant,
        sourceEntity: entity,
        isOverridden: true,
      } as ResolvedAssignmentEntity,
    }
  }
  if (override?.kind === 'aggregate') {
    return {
      resolvedEntity: {
        kind: 'aggregate',
        sourceEntity: override.entity,
        isOverridden: true,
      } as ResolvedAssignmentEntity,
    }
  }
  if (override?.kind === 'ignored') {
    return {
      resolvedEntity: toIgnoredResolvedEntity(entity, true),
    }
  }
  if (entity.kind === 'member') {
    const participant = picker.pickMember(entity, usedParticipantIds, blocked)
    if (!participant) {
      return {
        resolvedEntity: {
          kind: 'plain',
          sourceEntity: entity,
          issue: {
            type: 'unmatched_member',
            spellId,
            scopeId,
            requested: 1,
            available: picker.availableForMember(entity),
            missing: 1,
            memberName: entity.label,
          },
        } as ResolvedAssignmentEntity,
      }
    }
    usedParticipantIds.add(participant.participantId)
    onMemberParticipantResolved?.(participant, entity)
    return {
      resolvedEntity: {
        kind: 'participant',
        participant,
        sourceEntity: entity,
      } as ResolvedAssignmentEntity,
    }
  }
  if (entity.kind === 'by_role' || entity.kind === 'by_class') {
    if (autoMatchMode === 'preferred_and_explicit') {
      if (entity.kind === 'by_role') {
        const role = entity.role
        return {
          resolvedEntity: {
            kind: 'plain',
            sourceEntity: entity,
            issue: {
              type: 'insufficient_candidates',
              spellId,
              scopeId,
              role,
              requested: 1,
              available: 0,
              missing: 1,
            },
          } as ResolvedAssignmentEntity,
        }
      }
      return {
        resolvedEntity: {
          kind: 'plain',
          sourceEntity: entity,
          issue: {
            type: 'insufficient_candidates',
            spellId,
            scopeId,
            requested: 1,
            available: 0,
            missing: 1,
          },
        } as ResolvedAssignmentEntity,
      }
    }
    if (deferAssignableEntity) {
      return {
        deferredEntity: entity as DeferredResolverEntity,
      }
    }
    if (entity.kind === 'by_role') {
      const role = entity.role
      const participant = picker.pick(
        role,
        usedParticipantIds,
        blocked,
        boxRoleCounts,
      )
      if (!participant) {
        return {
          resolvedEntity: {
            kind: 'plain',
            sourceEntity: entity,
            issue: {
              type: 'insufficient_candidates',
              spellId,
              scopeId,
              role,
              requested: 1,
              available:
                role === 'anyone'
                  ? availableAnyoneCount
                  : picker.availableByRole(role),
              missing: 1,
            },
          } as ResolvedAssignmentEntity,
        }
      }
      usedParticipantIds.add(participant.participantId)
      return {
        resolvedEntity: {
          kind: 'participant',
          participant,
          sourceEntity: entity,
        } as ResolvedAssignmentEntity,
      }
    }
    const participant = picker.pickClass(
      entity.class_id,
      usedParticipantIds,
      blocked,
    )
    if (!participant) {
      return {
        resolvedEntity: {
          kind: 'plain',
          sourceEntity: entity,
          issue: {
            type: 'insufficient_candidates',
            spellId,
            scopeId,
            requested: 1,
            available: picker.availableByClass(entity.class_id),
            missing: 1,
          },
        } as ResolvedAssignmentEntity,
      }
    }
    usedParticipantIds.add(participant.participantId)
    return {
      resolvedEntity: {
        kind: 'participant',
        participant,
        sourceEntity: entity,
      } as ResolvedAssignmentEntity,
    }
  }
  if (
    entity.kind === 'everyone' ||
    entity.kind === 'collection' ||
    entity.kind === 'group' ||
    entity.kind === 'role'
  ) {
    return {
      resolvedEntity: {
        kind: 'aggregate',
        sourceEntity: entity,
      } as ResolvedAssignmentEntity,
    }
  }
  return {
    resolvedEntity: {
      kind: 'plain',
      sourceEntity: entity,
    } as ResolvedAssignmentEntity,
  }
}

export const resolveEventActivityTimelineAssignments = ({
  activityId,
  entries,
  encounterRows,
  abilities,
  autoMatchMode = 'full',
  spreadRoleCollections = true,
}: ResolveEventActivityTimelineAssignmentsInput) => {
  const allParticipants = toResolvedParticipants(entries, activityId)
  const resolvedByScopeBoxKey: Record<string, ResolvedAssignmentEntity[]> = {}
  const issues: EventActivityTimelineResolverIssue[] = []

  abilities.forEach((ability, abilityIndex) => {
    const row = encounterRows[abilityIndex]
    if (!row) return
    const assignmentGroups = getEffectiveAssignmentGroups(
      row.assignmentGroups,
      ability.assignment_groups,
    )
    const viewMode: AssignmentViewMode = getEffectiveAssignmentViewMode(
      ability.view_mode,
      row.entries.length,
    )
    const customGrouping = getEffectiveAbilityCustomGrouping(
      ability.spell_id,
      ability.custom_grouping,
    )
    const scopes = getAssignmentScopeIndexes(
      row.entries,
      viewMode,
      customGrouping,
      ability.assignment_type,
    )
    const remainingExplicitCharacterIds = scopes.reduce<Set<number>>(
      (acc, scope) => {
        const representativeCast = normalizeAssignmentCast(
          ability.casts?.[scope.castIndexes[0]],
          assignmentGroups,
        )
        for (let boxIndex = 0; boxIndex < assignmentGroups; boxIndex += 1) {
          const box = normalizeAssignmentBox(representativeCast.boxes[boxIndex])
          box.entities.forEach((entity) => {
            if (entity.kind === 'member') {
              acc.add(entity.character_id)
            }
          })
        }
        return acc
      },
      new Set<number>(),
    )
    const assignmentType = getEffectiveAssignmentType(ability.assignment_type)
    const interrupt = createInterruptContext(assignmentType, allParticipants)
    const immunity = createImmunityContext(assignmentType, allParticipants)
    const alternatingUsedParticipantIds = new Set<number>()
    const allSeparateRoundRobinPicker = createRoundRobinPicker(
      allParticipants,
      ['ranged', 'melee', 'healer', 'tank', 'dps'],
      interrupt,
      immunity,
    )
    const scopeBoxCapacities = scopes.map((scope) => {
      const representativeCast = normalizeAssignmentCast(
        ability.casts?.[scope.castIndexes[0]],
        assignmentGroups,
      )
      return Array.from({ length: assignmentGroups }, (_, boxIndex) =>
        expandedBoxSlotCount(
          normalizeAssignmentBox(representativeCast.boxes[boxIndex]),
          ability.expected_entities,
        ),
      )
    })
    scopes.forEach((scope, scopeIndex) => {
      const scopeParticipants =
        viewMode === 'alternating'
          ? allParticipants.filter(
              (participant) =>
                !alternatingUsedParticipantIds.has(participant.participantId),
            )
          : allParticipants
      const blockedParticipantIds = new Set(
        scopeParticipants
          .filter((participant) =>
            remainingExplicitCharacterIds.has(participant.characterId),
          )
          .map((participant) => participant.participantId),
      )
      const representativeCast = normalizeAssignmentCast(
        ability.casts?.[scope.castIndexes[0]],
        assignmentGroups,
      )
      const scopeAssignedParticipantIds = new Set<number>()
      const scopePicker =
        viewMode === 'all_separate'
          ? allSeparateRoundRobinPicker
          : createRoundRobinPicker(
              scopeParticipants,
              ['ranged', 'melee', 'healer', 'tank', 'dps'],
              interrupt,
              immunity,
            )
      scopePicker.setScopeTime(row.entries[scope.castIndexes[0]]?.time ?? 0)
      const availableAnyoneCount =
        interrupt || immunity
          ? scopePicker.availableByRole('anyone')
          : scopeParticipants.length
      const boxes = Array.from({ length: assignmentGroups }, (_, boxIndex) =>
        normalizeAssignmentBox(representativeCast.boxes[boxIndex]),
      )
      boxes.forEach((box, boxIndex) => {
        const expected = getEffectiveExpectedEntities(
          ability.expected_entities,
          box.expected_entities,
        )
        const missing = getAnyPlayerFillerCount(box.entities, expected)
        for (let i = 0; i < missing; i += 1) {
          box.entities.push({
            kind: 'by_role',
            role: 'anyone',
            id: `anyplayer-filler-${boxIndex}-${i}`,
          })
        }
      })
      const remainingRosterCount =
        viewMode === 'alternating'
          ? allParticipants.filter(
              (participant) =>
                !alternatingUsedParticipantIds.has(participant.participantId),
            ).length
          : allParticipants.length
      const remainingScopeCapacities =
        viewMode === 'alternating'
          ? scopeBoxCapacities
              .slice(scopeIndex)
              .map((boxCapacities) =>
                boxCapacities.reduce((sum, count) => sum + count, 0),
              )
          : [
              (scopeBoxCapacities[scopeIndex] ?? []).reduce(
                (sum, count) => sum + count,
                0,
              ),
            ]
      const scopeSlotCount = boxes.reduce(
        (sum, box) => sum + box.entities.length,
        0,
      )
      const scopeShare =
        waterFill(remainingRosterCount, remainingScopeCapacities)[0] ?? 0
      const ignoreCount = Math.max(0, scopeSlotCount - scopeShare)
      const resolvedByBox = boxes.map((box) =>
        Array.from(
          { length: box.entities.length },
          (): ResolvedAssignmentEntity | null => null,
        ),
      )
      const pendingResolverEntities: {
        boxIndex: number
        slotIndex: number
        entity: DeferredResolverEntity
      }[] = []
      const roleCountByBox: Record<
        number,
        Partial<Record<RoleKey, number>>
      > = {}
      const balanceRolesAcrossBoxes = assignmentGroups > 1
      const recordResolvedParticipant = (
        boxIndex: number,
        resolvedEntity: ResolvedAssignmentEntity,
      ) => {
        if (resolvedEntity.kind !== 'participant') return
        const counts = (roleCountByBox[boxIndex] ??= {})
        const role = resolvedEntity.participant.role
        counts[role] = (counts[role] ?? 0) + 1
      }
      const participantCountForBox = (boxIndex: number) =>
        resolvedByBox[boxIndex].filter(
          (entity) => entity?.kind === 'participant',
        ).length
      const hasReachedScopeShare = () =>
        autoMatchMode === 'full' &&
        scopeShare > 0 &&
        scopeAssignedParticipantIds.size >= scopeShare
      const maxSlotCount = boxes.reduce(
        (maxValue, box) => Math.max(maxValue, box.entities.length),
        0,
      )

      for (let slotIndex = 0; slotIndex < maxSlotCount; slotIndex += 1) {
        for (let boxIndex = 0; boxIndex < assignmentGroups; boxIndex += 1) {
          const entity = boxes[boxIndex].entities[slotIndex]
          if (!entity) continue
          const { resolvedEntity, deferredEntity } =
            resolveAssignmentEntityWithPicker({
              entity,
              picker: scopePicker,
              usedParticipantIds: scopeAssignedParticipantIds,
              blockedParticipantIds,
              spellId: ability.spell_id,
              scopeId: scope.id,
              availableAnyoneCount,
              deferAssignableEntity: true,
              autoMatchMode,
              onMemberParticipantResolved: (participant, memberEntity) => {
                if (participant.characterId === memberEntity.character_id) {
                  remainingExplicitCharacterIds.delete(
                    memberEntity.character_id,
                  )
                }
              },
            })
          if (deferredEntity) {
            pendingResolverEntities.push({
              boxIndex,
              slotIndex,
              entity: deferredEntity,
            })
            continue
          }
          if (!resolvedEntity) continue
          recordResolvedParticipant(boxIndex, resolvedEntity)
          resolvedByBox[boxIndex][slotIndex] = resolvedEntity
        }
      }

      while (pendingResolverEntities.length > 0) {
        const rankedEntities = pendingResolverEntities
          .map((entry) => {
            if (entry.entity.kind === 'by_role') {
              const role = entry.entity.role
              return {
                ...entry,
                available: scopePicker.availableAssignableByRole(
                  role,
                  scopeAssignedParticipantIds,
                  blockedParticipantIds,
                ),
              }
            }
            return {
              ...entry,
              available: scopePicker.availableAssignableByClass(
                entry.entity.class_id,
                scopeAssignedParticipantIds,
                blockedParticipantIds,
              ),
            }
          })
          .sort((left, right) => {
            const leftFillable = left.available > 0 ? 0 : 1
            const rightFillable = right.available > 0 ? 0 : 1
            if (leftFillable !== rightFillable) {
              return leftFillable - rightFillable
            }
            if (left.available !== right.available) {
              return left.available - right.available
            }
            const leftResolved = participantCountForBox(left.boxIndex)
            const rightResolved = participantCountForBox(right.boxIndex)
            if (leftResolved !== rightResolved) {
              return leftResolved - rightResolved
            }
            if (left.slotIndex !== right.slotIndex) {
              return left.slotIndex - right.slotIndex
            }
            return left.boxIndex - right.boxIndex
          })
        const nextEntry = rankedEntities[0]
        if (!nextEntry) break
        const pendingIndex = pendingResolverEntities.findIndex(
          (entry) =>
            entry.boxIndex === nextEntry.boxIndex &&
            entry.slotIndex === nextEntry.slotIndex,
        )
        if (pendingIndex >= 0) {
          pendingResolverEntities.splice(pendingIndex, 1)
        }
        if (hasReachedScopeShare()) {
          continue
        }
        const { resolvedEntity } = resolveAssignmentEntityWithPicker({
          entity: nextEntry.entity,
          picker: scopePicker,
          usedParticipantIds: scopeAssignedParticipantIds,
          blockedParticipantIds,
          spellId: ability.spell_id,
          scopeId: scope.id,
          availableAnyoneCount,
          autoMatchMode,
          boxRoleCounts: balanceRolesAcrossBoxes
            ? (roleCountByBox[nextEntry.boxIndex] ??= {})
            : undefined,
        })
        if (!resolvedEntity) continue
        recordResolvedParticipant(nextEntry.boxIndex, resolvedEntity)
        resolvedByBox[nextEntry.boxIndex][nextEntry.slotIndex] = resolvedEntity
      }

      applyExcessIgnoredSlots({
        boxes,
        resolvedByBox,
        ignoreCount: autoMatchMode === 'full' ? ignoreCount : 0,
      })
      issues.push(
        ...resolvedByBox.flatMap((resolvedEntities) =>
          resolvedEntities.flatMap((entity) =>
            entity?.issue ? [entity.issue] : [],
          ),
        ),
      )

      boxes.forEach((box, boxIndex) => {
        const key = `${row.abilityKey}:${scope.id}:${boxIndex}`
        const resolved = resolvedByBox[boxIndex].map(
          (resolvedEntity, index) =>
            resolvedEntity ?? {
              kind: 'plain' as const,
              sourceEntity: box.entities[index],
            },
        )
        resolvedByScopeBoxKey[key] = spreadRoleCollections
          ? expandSpreadRoleCollections(
              resolved,
              scopeParticipants,
              scopeAssignedParticipantIds,
            )
          : resolved
      })
      if (viewMode === 'alternating') {
        scopeAssignedParticipantIds.forEach((participantId) =>
          alternatingUsedParticipantIds.add(participantId),
        )
      }
    })
  })

  return {
    resolvedByScopeBoxKey,
    issues,
  }
}

type ResolveEventActivityCastTargetsInput = {
  activityId: number
  entries: EventActivityParticipantEntry[]
  actors: RaidPlanActor[]
  generalCasts?: RaidPlanCast[]
  castTargetOverrides?: EventPlanCastTargetOverrides
  autoMatchMode?: EventActivityAutoMatchMode
  overrideParticipants?: ResolvedAssignmentParticipant[]
}

type ResolveEventActivityCastTargetsResult = {
  resolvedByCastId: Record<string, ResolvedAssignmentEntity[]>
  issues: EventActivityTimelineResolverIssue[]
}

export const resolveEventActivityCastTargets = ({
  activityId,
  entries,
  actors,
  generalCasts,
  castTargetOverrides,
  autoMatchMode = 'full',
  overrideParticipants,
}: ResolveEventActivityCastTargetsInput): ResolveEventActivityCastTargetsResult => {
  const participants = toResolvedParticipants(entries, activityId)
  const participantByCharacterId = (
    overrideParticipants ?? participants
  ).reduce<Record<number, ResolvedAssignmentParticipant>>(
    (acc, participant) => {
      acc[participant.characterId] = participant
      return acc
    },
    {},
  )
  const allCasts = [
    ...actors.flatMap((actor) => actor.casts),
    ...(generalCasts ?? []),
  ]
  const resolvedByCastId: Record<string, ResolvedAssignmentEntity[]> = {}
  const issues: EventActivityTimelineResolverIssue[] = []
  allCasts.forEach((cast) => {
    const targets = cast.targets ?? []
    if (!targets.length) return
    const picker = createRoundRobinPicker(participants, [
      'ranged',
      'melee',
      'healer',
      'tank',
      'dps',
    ])
    const usedParticipantIds = new Set<number>()
    const resolvedEntities = targets.flatMap((group, groupIndex) =>
      group.map((entity, entityIndex) => {
        const entityId = `${groupIndex}:${entityIndex}`
        const override = getCastTargetEntityOverride({
          castTargetOverrides,
          castId: cast.id,
          entityId,
        })
        const { resolvedEntity } = resolveAssignmentEntityWithPicker({
          entity,
          picker,
          usedParticipantIds,
          spellId: cast.spell_id ?? 0,
          scopeId: cast.id,
          availableAnyoneCount: participants.length,
          participantByCharacterId,
          override,
          autoMatchMode,
        })
        if (!resolvedEntity) {
          return {
            kind: 'plain',
            sourceEntity: entity,
          } as ResolvedAssignmentEntity
        }
        if (resolvedEntity.issue) {
          issues.push(resolvedEntity.issue)
        }
        return resolvedEntity
      }),
    )
    resolvedByCastId[cast.id] = resolvedEntities
  })
  return {
    resolvedByCastId,
    issues,
  }
}
